import { action, computed, observable } from 'mobx';
import get from 'lodash/get';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { Explorer } from '../models/configuration/explorer/Explorer';
import { FundGroupHeader } from '../models/configuration/FundGroupHeader';
import { ExplorerWatchlist } from '../models/configuration/explorer/ExplorerWatchlist';
import { generateGuid } from '../utils/generateGuid';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { snakeCase } from 'lodash';
import { CultureSearchTransform } from '../models/configuration/CultureSearchTransform';
import { GlobalConfig } from '../models/configuration/GlobalConfig';
import { ConditionalDataConfig } from '../models/configuration/ConditionalDataConfig';
import { IncludeData } from '../models/configuration/IncludeData';

export class ConfigStore extends BaseStore {
    @observable configFile = {} as any;
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    @observable appDataProps = {};
    fundlist;

    constructor(
        stores: Stores,
        configFile,
        currentModuleId,
        currentFundType,
        fundlist,
        appDataProps
    ) {
        super(stores);
        this.configFile = configFile;
        this.currentModuleId = currentModuleId;
        this.currentFundType = currentFundType;
        this.fundlist = fundlist;
        this.appDataProps = appDataProps;
    }

    // TOP-LEVEL FUND TYPE
    // ===================

    // Sets the current module
    @action
    setFundType = fundType => {
        this.currentFundType = fundType.fundType;
    };

    // Get showFundTypes
    @computed
    get showFundTypes() {
        return this.configFile.showFundTypes || false;
    }

    // Get fund types
    @computed
    get fundTypes() {
        const { fundTypes = [] } = this.configFile;

        return fundTypes;
    }

    // Get fundTypeProperty
    @computed
    get fundTypeProperty() {
        const { fundTypeProperty } = this.configFile;

        return fundTypeProperty;
    }

    // Get applicationName
    @computed
    get applicationName() {
        return this.configFile.applicationName;
    }

    // Returns selectedFundType for either the currentFundType, the first fundType set as selected, or the first fundType
    @computed
    get selectedFundType() {
        const fundType =
            this.fundTypes.find(f =>
                this.currentFundType
                    ? f.fundType === this.currentFundType
                    : f.selected
            ) ||
            this.fundTypes[0] ||
            {};

        return fundType;
    }

    // MODULES FOR SELECTED FUND TYPE
    // ==============================

    // Sets the current module
    @action
    setModule = module => {
        this.currentModuleId = module.id;
    };

    // Get showFundTypes
    @computed
    get showModules() {
        return this.selectedFundType.showModules || false;
    }

    // Get showFundTypes
    @computed
    get moduleMobileSelector() {
        return this.selectedFundType.moduleMobileSelector || 'tabs';
    }

    // Returns available modules from selectedFundType
    @computed
    get modules() {
        const { modules = [] } = this.selectedFundType;
        return modules;
    }

    // Returns explorer related configurations
    @computed
    get explorer(): Explorer {
        return this.config.explorer;
    }

    // Returns selectedModule for either the currentModuleId, the first module set as selected, or the first module
    @computed
    get selectedModule() {
        const module =
            this.modules.find(m =>
                this.currentModuleId
                    ? m.id === this.currentModuleId
                    : m.selected
            ) ||
            this.modules[0] ||
            {};

        return module;
    }

    @computed
    get isDocumentModule() {
        const { type } = this.selectedModule;
        return type === 'DocumentList';
    }

    // TODO: Rename config => selectedModule Config
    // CONFIG FOR SELECTED MODULE
    // ==========================

    // Returns config for the selected module
    @computed
    get config() {
        const { config = {} } = this.selectedModule;

        return config;
    }

    // VALUES DERIVED FROM CONFIG FOR SELECTED MODULE
    // ==============================================

    @computed
    get noDataText(): string {
        return (
            this.config.noDataText ||
            this.selectedFundType.noDataText ||
            'There are no funds matching this search'
        );
    }

    @computed
    get stickyOffset() {
        const { stickyOffset } = this.fundGroupHeader;
        return stickyOffset || this.config.stickyOffset || 0;
    }

    @computed
    get defaultCulture() {
        return (
            this.config.defaultCulture ||
            this.globalConfig.defaultCulture ||
            'en-GB'
        );
    }

    @computed
    get sourceCulture() {
        return (
            (this.globalConfig && this.globalConfig.sourceCulture) || 'Default'
        );
    }

    @computed
    get useHtmlTranslations() {
        return (
            (this.globalConfig && this.globalConfig.useHtmlTranslations) ||
            false
        );
    }

    @computed
    get limit() {
        return this.config.searchLimit || 30;
    }

    @computed
    get sort() {
        const configSort = this.config.sort || [];
        // replace "app:" place holders with app data props
        const parsedSort = configSort.map(item => {
            item.key = snakeCase(
                replacePlaceholders({
                    text: item.key,
                    appParams: this.appDataProps,
                })
            );
            return item;
        });
        return parsedSort;
    }

    @computed
    get advancedSort() {
        return this.config.advancedSort;
    }

    @computed
    get watchListProperty() {
        const property =
            (this.watchListConfig && this.watchListConfig.property) ||
            this.config.watchListProperty;
        return property;
    }

    @computed
    get watchListEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.config.watchListEnabled;
        return enabled;
    }

    @computed
    get watchListConfig(): ExplorerWatchlist {
        return (
            this.config &&
            this.config.explorer &&
            this.config.explorer.watchlist
        );
    }

    @computed
    get responsiveConfig(): any {
        return this.config && this.config.responsiveConfig;
    }

    @computed
    get fundGroupHeader(): FundGroupHeader {
        const fundGroupHeader = this.config.fundGroupHeader || {};
        const fundGroupProperty = this.config.fundGroupProperty;
        const stickyOffset = this.config.stickyOffset || 0;
        return {
            fundGroupProperty,
            stickyOffset,
            ...fundGroupHeader,
        };
    }

    @computed
    get fundGroupProperty() {
        const { fundGroupProperty } = this.fundGroupHeader;
        return fundGroupProperty || this.config.fundGroupProperty;
    }

    @computed
    get fundCurrencyProperty() {
        return this.config.fundCurrencyProperty;
    }

    @computed
    get identifierSearch() {
        return this.config.identifierSearch || {};
    }

    @computed
    get documentsJoinSpec() {
        return this.config.documentsJoinSpec || [];
    }

    @computed
    get timeseriesInclude() {
        const { timeseriesInclude } = this.config;
        if (this.isExcluded(timeseriesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return timeseriesInclude || {};
    }

    @computed
    get allocationsInclude() {
        const { allocationsInclude } = this.config;
        if (this.isExcluded(allocationsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return allocationsInclude || {};
    }

    @computed
    get statisticsInclude() {
        const { statisticsInclude } = this.config;
        if (this.isExcluded(statisticsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return statisticsInclude || {};
    }

    @computed
    get commentariesInclude() {
        const { commentariesInclude } = this.config;
        if (this.isExcluded(commentariesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return commentariesInclude || {};
    }

    @computed
    get disclaimersInclude() {
        const { disclaimersInclude } = this.config;
        if (this.isExcluded(disclaimersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return disclaimersInclude || {};
    }

    @computed
    get fundManagersInclude() {
        const { fundManagersInclude } = this.config;
        if (this.isExcluded(fundManagersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return fundManagersInclude || {};
    }

    @computed
    get includeProperties() {
        const { includeProperties } = this.config;
        return includeProperties;
    }

    isExcluded(includeData: IncludeData) {
        return includeData && includeData.exclude;
    }

    @computed
    get filterOptions() {
        if (this.config.filterConfig) {
            return this.config.filterConfig;
        }

        const filtersTopExpanded = this.config.hasOwnProperty(
            'filtersTopExpanded'
        )
            ? this.config.filtersTopExpanded
            : true;

        const filtersTopExpandable = this.config.hasOwnProperty(
            'filtersTopExpandable'
        )
            ? this.config.filtersTopExpandable
            : true;

        return {
            filtersTopExpanded,
            filtersTopExpandable,
            top: this.config.filtersTop,
            clearIndividualFilters: this.config.clearIndividualFilters,
            filters: this.config.filters,
            minimumFiltersNeeded:
                this.config.hasOwnProperty('minimumFiltersNeeded') &&
                this.config.minimumFiltersNeeded,
        };
    }

    @computed
    get filtersConfig() {
        return this.filterOptions.filters || [];
    }

    @computed
    get clearIndividualFilters() {
        return this.filterOptions.clearIndividualFilters;
    }

    @computed
    get filtersTop() {
        return this.filterOptions.top;
    }

    @computed
    get clearFiltersLabel() {
        return this.filterOptions.clearFiltersLabel;
    }

    @computed
    get filtersTopExpanded() {
        return this.filterOptions.hasOwnProperty('topExpanded')
            ? this.filterOptions.topExpanded
            : true;
    }

    @computed
    get filtersTopExpandable() {
        return this.filterOptions.hasOwnProperty('topExpandable')
            ? this.filterOptions.topExpandable
            : true;
    }

    @computed
    get documentSearch() {
        return this.config.documentSearch || [];
    }

    @computed
    get globalDocumentSearch() {
        return this.config.globalDocumentSearch || [];
    }

    @computed
    get documentSearchBySegmentation() {
        return get(this.config.documentSearchBySegmentation, this.fundlist, []);
    }

    @computed
    get documentSegmentationFilters() {
        return get(this.config.documentSegmentationFilters, this.fundlist, {});
    }

    @computed
    get documentGroupMeta() {
        return this.config.documentGroupMeta || '';
    }

    @computed
    get documentGroupOrder() {
        return this.config.documentGroupOrder || [];
    }

    @computed
    get noDataPlaceholder() {
        return this.config.noDataPlaceholder || '–';
    }

    @computed
    get cultureAliases() {
        const globalCultureAliases =
            this.globalConfig &&
            this.globalConfig.valueAliases &&
            this.globalConfig.valueAliases.cultures;
        return { ...globalCultureAliases, ...this.config.cultureAliases } || {};
    }

    @computed
    get selectedDocumentsLabel() {
        return (
            this.config.selectedDocumentsLabel || {
                singular: `{selected}/{total} document selected`,
                plural: `{selected}/{total} documents selected`,
            }
        );
    }

    @computed
    get availableDocumentsLabel() {
        return (
            this.config.availableDocumentsLabel || {
                singular: `{total} document available`,
                plural: `{total} documents available`,
            }
        );
    }

    @computed
    get columnConfig() {
        const columnConfig = this.config.columnConfig || [];
        // Create unique key for link filtering
        for (const column of columnConfig) {
            column.key = column.key || generateGuid();
        }
        return columnConfig;
    }

    @computed
    get headerDisclaimerTypes() {
        return this.config.headerDisclaimerTypes || [];
    }

    // If present, config.footerDisclaimerTypes takes precedence over config.disclaimerTypes
    // config.disclaimerTypes should be considered a legacy option
    @computed
    get footerDisclaimerTypes() {
        return (
            this.config.footerDisclaimerTypes ||
            this.config.disclaimerTypes ||
            []
        );
    }

    @computed
    get rowConfig() {
        const rowConfig = this.config.rowConfig;
        // Create unique key for link filtering.
        if (rowConfig && rowConfig.header) {
            rowConfig.header.key = rowConfig.header.key || generateGuid();
        }

        return rowConfig || {};
    }

    @computed
    get performanceCheck() {
        return this.config.performanceCheck || {};
    }

    @computed
    get segmentationFilter() {
        return this.config.segmentationFilter || {};
    }

    @computed
    get documentFilename() {
        return this.config.documentFilename || '';
    }

    @computed
    get documentDisplayName() {
        return this.config.documentDisplayName;
    }

    @computed
    get globalConfig(): GlobalConfig {
        return this.configFile && this.configFile.globalConfig;
    }

    @computed
    get searchMillisecondTimeout(): number {
        if (this.globalConfig && this.globalConfig.searchMillisecondTimeout) {
            return this.globalConfig.searchMillisecondTimeout;
        }
        return 600;
    }

    @computed
    get autoSearchMinChar(): number {
        if (
            this.config &&
            this.config.search &&
            this.config.search.config &&
            this.config.search.config.autoSearchMinChar
        ) {
            return this.config.search.config.autoSearchMinChar;
        }
        if (this.config && this.config.autoSearchMinChar) {
            return this.config.autoSearchMinChar;
        }
        if (this.globalConfig && this.globalConfig.autoSearchMinChar) {
            return this.globalConfig.autoSearchMinChar;
        }

        return 1;
    }

    @computed
    get disabledEntityFlag(): ConditionalDataConfig | undefined {
        return this.globalConfig && this.globalConfig.disabledEntityFlag;
    }

    @computed
    get cultureSearchTransform(): CultureSearchTransform | undefined {
        if (this.config && this.config.cultureSearchTransform) {
            return this.config.cultureSearchTransform;
        }
        if (this.globalConfig && this.globalConfig.cultureSearchTransform) {
            return this.globalConfig.cultureSearchTransform;
        }
        return;
    }

    @computed
    get shareClassKey(): string {
        return (this.globalConfig && this.globalConfig.shareClassKey) || 'isin';
    }
}



// WEBPACK FOOTER //
// ./src/stores/ConfigStore.ts